import React from 'react';
import { EVENTS } from 'shared/constants/analytics';
import {
  searchImpressions,
  syncSearchImpressionContext,
} from 'shared/helpers/src/search-impressions';
import { buildSearchImpressionProperties } from 'shared/helpers/src/search-impression-properties';
import type {
  SearchImpressionItem,
  SearchImpressionOptions,
} from 'shared/helpers/src/search-impression-properties';
import { useAnalyticsImpression } from 'src/hooks/analytics/use-analytics-impression';

interface SearchImpressionParams {
  item: SearchImpressionItem;
  resultType: string;
  contextId?: string;
  options?: SearchImpressionOptions;
}

// Decides what a row should report, so the decision is reachable without a DOM. Resolves
// during render: the id has to be current for the click that may follow it.
export function resolveSearchImpression({
  item,
  resultType,
  contextId,
  options = {},
}: SearchImpressionParams) {
  syncSearchImpressionContext(contextId);

  const itemId = item.id || item._id;
  const impressionId = searchImpressions.getImpressionId(itemId, resultType, contextId);

  // Without a context there is no id to dedup on, and the impression would join to
  // nothing — so don't report it, the same guard mobile applies.
  const reportable = !!contextId && !searchImpressions.wasReported(impressionId);

  return {
    impressionId,
    event: reportable ? EVENTS.SEARCH.RESULTS_IMPRESSION : null,
    ...(reportable && {
      eventPayload: buildSearchImpressionProperties(item, resultType, contextId, options),
    }),
  };
}

export function markSearchImpressionReported(impressionId?: string) {
  searchImpressions.markReported(impressionId);
}

export function useSearchImpression(params: SearchImpressionParams) {
  const resolved = resolveSearchImpression(params);
  const resolvedImpressionId = resolved.impressionId;

  const onTrack = React.useCallback(
    () => markSearchImpressionReported(resolvedImpressionId),
    [resolvedImpressionId],
  );

  const { ref, impressionId } = useAnalyticsImpression({ ...resolved, onTrack });

  return { ref, impressionId };
}
